import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { MDBRow, MDBCol } from "mdbreact";
import BlendFuelRegistry from "./SubForms/BlendFuelRegistry";
import _ from "lodash";
import { FeatureType } from "../../constants/FeatureToggles";
import { ClaimsContext } from "../../services/ClaimsService";
import "./VesselDetails.scss";
import VesselParticularsFormGroup from "./SubForms/VesselParticularsFormGroup";
import VesselIdentificationFormGroup from "./SubForms/VesselIdentificationFormGroup";
import ManagementSubmitterFormGroup from "./SubForms/ManagementSubmitterFormGroup";
import UKMETSFormGroup from "./SubForms/UkmetsMonitoringPlan";
import RegulationDetailsmFormGroup from "./SubForms/RegulationDetailsFormGroup";
import CIICorrectionFactorsFormGroup from "./SubForms/CIICorrectionFactorsFormGroup";
import CommentsFormGroup from "./SubForms/CommentsFormGroup";
import axiosHttp from "../../services/interceptor";
import { emitToasterMsg } from "../../services/toasterUtility";
import { getMasterData } from "../../store/myFleet/myFleet.actions";
import BreadCrumb from "../../common/breadCrumb/BreadCrumb";
import { Button, COLORS, Modal } from "abs-ui-components";
import ConsumerRegistryFormGroup from "./SubForms/ConsumerRegistryFormGroup";
import FuelRegistryFormGroup from "./SubForms/FuelRegistryFormGroup";
import WapsFormGroup from "./SubForms/WapsFormGroup";
import CorrectionsElectricPowerFormGroup from "./SubForms/CorrectionsElectricPowerFormGroup";
import {
	VesselDetailsModel,
	ManagementSubmitterFormGroupModel,
	VesselIdentificationFormGroupModel,
	VesselParticularsFormGroupModel,
	ConsumerRegistryFormGroupModel,
	FuelRegistryFormGroupModel,
	UKMETSFormGroupModel
} from "./VesselDetailsModel";

const baseURL = process.env.REACT_APP_BASE_URL;


const FormTitle = ({ title }) => (
	<div>
		<h4 className="card-header">{title}</h4>
	</div>
);

const VesselDetails = ({ vesselData, vesselMstId, disabled, isParentReport, isEnrollVessel, regulations, disableRiskAssessment,isReportTab,reportStatus}) => {
	const navigate = useNavigate();
	const methods = useForm({
		shouldFocusError: true,
	});
	const dispatch = useDispatch();

	const masterData = useSelector((state) => state.myFleet.masterData);
	//maintaining state
	const [imoBasedData, setImoBasedData] = useState(null);
	const [sumofMainEnginePowers, setSumofMainEnginePowers] = useState(vesselData?.mainPropulsionPower);
	const [sumofAuxEnginePowers, setSumofAuxEnginePowers] = useState(vesselData?.auxEnginePower);
	const [isLoading, setIsLoading] = useState(false);
	const [openModal, isOpenModal] = useState(false);
	const [childPayload, setChildPayload] = useState();

	//get consumer payload from redux store
	const consumerRegistryPayload = useSelector((state) => state.myFleet.consumerRegistryPayload);
	//get fuel payload from redux store
	const fuelRegistryPayload = useSelector((state) => state.myFleet.fuelRegistryPayload);
	const isVerificationComplete = reportStatus==='Verification Completed'?true:false;

	// hook to get all drop downs data from API using redux store
	useEffect(() => {
		if (Object.keys(masterData).length === 0) {
			dispatch(getMasterData());
		}
	}, []);

	//bread crumb
	const breadcrumb = useMemo(
		() => [
			{ label: "My Fleet", url: "/my-fleet", navId: "myfleet_navId" },
			{
				label: vesselMstId ? "Edit Vessel" : "Enroll Vessel",
				url: "",
				navId: "enroll_vessel_navId",
			},
		],
		[]
	);

	if (consumerRegistryPayload === "undefined" || undefined) {
		return [];
	}

	if (fuelRegistryPayload === "undefined" || undefined) {
		return [];
	}

	const findFirstErrorField = (errors, path = []) => {
		if (!errors || typeof errors !== "object") return null;

		for (const [key, value] of Object.entries(errors)) {
			const nextPath = [...path, key];

			if (value?.message || value?.type) {
				return nextPath.join(".");
			}

			if (value && typeof value === "object") {
				const nestedPath = findFirstErrorField(value, nextPath);
				if (nestedPath) return nestedPath;
			}
		}

		return null;
	};

	const handleFormError = (errors) => {
		const fieldName = findFirstErrorField(errors);

		if (!fieldName) return;

		const element =
			document.querySelector(`[name="${fieldName}"]`);
		if (element) {
			element.scrollIntoView({
				behavior: "smooth",
				block: "center",
			});

			if (
				element.tagName === "INPUT" ||
				element.tagName === "TEXTAREA" ||
				element.tagName === "SELECT"
			) {
				element.focus();
			} else {
				const input = element.querySelector("input");
				input?.focus();
			}
		}
	};

	//check for value is not falsy
	const transformData = (data, labelKey, valueKey, type) => {
		const formattedData = [];
		Object.entries(data).forEach(([key, value]) => {
			if (value !== "" && value !== "NaN" && value !== undefined && value !== "null") {
				formattedData.push({
					vesselMstId: type === "enroll" ? 0 : vesselMstId,
					[labelKey]: key,
					[valueKey]: parseFloat(value),
				});
			}
		});
		return formattedData;
	};

	// Fetches vessel details by vesselMstId and handles errors with a toaster message.
	const getVesselDetailsByVesselMstId = async (vesselMstId) => {
		try {
			const response = await axiosHttp.get(`${baseURL}api/MyFleet/GetVesselById?vesselMstId=${vesselMstId}`);
			if (response?.data) {
				return response?.data;
			}
		} catch (error) {
			emitToasterMsg("error", "Error", error?.message);
		}
	};

	// Converts string numbers to floats and replaces falsy values with 0.
	const parseFields = (data) => {
		const parseValue = (value) => (typeof value === "string" && !isNaN(value) ? parseFloat(value) : value);

		return Object.fromEntries(Object.entries(data).map(([key, value]) => [key, parseValue(value) || 0]));
	};

	// navigate back to fleet page on cancel button click
	const handleCancel = () => {
		navigate("/my-fleet");
	};

	//method to filter out invalid output power values
	const transformedEngineList = (data) => {
		return {
			vesselEnginePowerRequests: data
				.map((item) => {
					let engineType = item.auxEnginePower ? "AE" : "ME";
					let engineName = item.auxEnginePower || item.mainEnginePower;
					let outputPower = item.auxEnginePowerValue || item.mainEnginePowerValue;

					return {
						vesselMstId: item.vesselMstId,
						engineType: engineType,
						engineName: engineName,
						outputPower: outputPower,
					};
				})
				.filter((item) => {
					return !isNaN(item.outputPower) && item.outputPower !== false && item.outputPower != null;
				}),
		};
	};

	// method to create payload for vessel form
	const handleAddVesselPayload = (payloadData, type) => {
		const getMediumId = masterData[0]?.GetRiskAssessment?.filter((ele) => ele?.Value.toLowerCase() === "medium");
		const inherentRisk = payloadData.riskAssessment.inherentRiskAssessmentMstId;
		const controlRisk = payloadData.riskAssessment.controlRiskAssessmentMstId;

		const vesselAttributes = parseFields(payloadData.vesselAttributes);
		const vesselCapacityChangeRequests =
			payloadData.vesselCapacityChangeRequests
				?.map((item, index) => ({
					...item,
					changeType: index === 0 ? 1 : 2,
				}))
				.filter(item => item.hasChanged)
				.map(item => ({
					...item,
					vesselMstId: type === "enroll" ? 0 : vesselMstId,
					hasChanged: item.hasChanged,
					oldValue: Number(item.oldValue),
					changeReason: Number(item.changeReason),
      				reviewNeeded: Boolean(item.reviewNeeded),
      				reportYear: item.reportYear || null,
					updatedBy: item.updatedBy || null,
					updatedOn: item.updatedOn || null,
      				remarks:  item.remarks || null,
				})) || [];
		const vesselTechnicalEfficiencyRequests = payloadData.technicalEfficiencyList
			.filter((item) => item?.technicalEfficiencyMstId?.TechnicalEfficiencyMstId)
			.map((item) => {
				let data = {
					vesselMstId: type === "enroll" ? 0 : vesselMstId,
					technicalEfficiencyType: "",
					technicalEfficiencyMstId: item?.technicalEfficiencyMstId?.TechnicalEfficiencyMstId ?? "",
					technicalEfficiencyText: item?.technicalEfficiencyText ? item?.technicalEfficiencyText : "",
				};
				return data;
			});
		const vesselAuxEnginePowerRequests = transformData(payloadData.auxEngineList, "auxEnginePower", "auxEnginePowerValue", type);
		const vesselMainEnginePowerRequests = transformData(payloadData.mainEngineList, "mainEnginePower", "mainEnginePowerValue", type);
		const vesselEngineTypeRequest = transformedEngineList([...vesselAuxEnginePowerRequests, ...vesselMainEnginePowerRequests]);

		//creating payload for fuel registry
		const convertedPayload = (fuelData) => {
			return fuelData.map((fuelItem) => ({
				vesselMstId: fuelItem.VesselMstId,
				fuelType: fuelItem.FuelType,
				fuelName: fuelItem.FuelName,
				lcv: parseFloat(fuelItem.Lcv),
				co2eq: parseFloat(fuelItem.Co2eq),
				cf: parseFloat(fuelItem.Cf),
				efCo2: parseFloat(fuelItem.EfCo2),
				efCh4: parseFloat(fuelItem.EfCh4),
				efN2o: parseFloat(fuelItem.EfN2o),
				rwd: parseFloat(fuelItem.Rwd),
				cslip: fuelItem.Cslip === "Applicable",
				isEthanol: fuelItem?.IsEthanol,
			}));
		};

		//method to restrict null rows for consumer registry section
		const removeNullRows = (consumers) => {
			const filteredConsumers = consumers.filter((consumer) => (consumer?.consumerType || consumer?.consumerCode) && consumer?.consumerName);
			return filteredConsumers;
		};

		//creating paylod for vessel form
		const enrollVesselPayload = {
			// (future scope)
			// vesselMstId: vesselMstId ? vesselMstId : 0,
			imoNumber: `${payloadData.vesselIdentifications.imoNumber}`,
			vesselName: payloadData.vesselIdentifications.vesselName?.toUpperCase(),
			callSign: payloadData.vesselIdentifications.callSign?.toUpperCase(),
			imoVesselTypeMstId: payloadData.vesselIdentifications.imovesselTypeId?.ImoVesselTypeMstId,
			mrvVesselTypeMstId: payloadData.vesselIdentifications.mrvvesselTypeId?.MrvVesselTypeMstId,
			ukVesselTypeMstId: payloadData.vesselIdentifications.ukvesselTypeId?.UkVesselTypeMstId,
			
			portCodeMstId: payloadData.vesselIdentifications.portOfRegistry?.portCodeMstId,
			submitterWcnMstId: Number(payloadData.vesselIdentifications.managementCompanyWCN?.wcnMappingId),
			alternativeWcnMstId: payloadData.vesselIdentifications.submitterWCN?.wcnMappingId || null,
			remarks: payloadData.vesselIdentifications.comments || "",
			inherentRiskAssessmentMstId: inherentRisk,
			controlRiskAssessmentMstId: controlRisk,
			combinedRiskAssessmentMstId: getMediumId[0].RiskAssessmentMstId,

			grossTonnage: vesselAttributes.grossTonnage,
			netTonnage: vesselAttributes.netTonnage,
			deadWeight: vesselAttributes.deadweight,
			totalRatedMainPropulsionPowerOutput: vesselAttributes.totalRatedMainPropulsionPowerOutput,
			totalRatedAuxiliaryEnginePowerOutput: vesselAttributes.totalRatedAuxiliaryEnginePowerOutput,
			lengthBetweenPerpendiculars: vesselAttributes.lengthBetweenPerpendiculars === "null" ? 0 : vesselAttributes.lengthBetweenPerpendiculars ?? 0,
			beam: vesselAttributes.beam === "null" ? 0 : vesselAttributes.beam ?? 0,
			maximumDraughtAtSummerLoadLine:
				vesselAttributes.maximumDraughtAtSummerLoadLine === "null" ? 0 : vesselAttributes.maximumDraughtAtSummerLoadLine ?? 0,
			yearOfDelivery: vesselAttributes.yearOfDelivery,
			iceClassMstId: +payloadData.regulationDetails.iceClass.IceClassMstId,
			otherIceClassName: payloadData.regulationDetails.otherIceClassName || null,
			primaryMonitoringMethodMstId: +payloadData?.regulationDetails?.monitoringMethodPrimary?.MonitoringMethodMstId,
			secondaryMonitoringMethodMstId: null,
			otherMrvVesselTypeName: payloadData.vesselIdentifications.otherMrvVesselTypeName || null,
			sfocReefer: vesselAttributes.sfocReefer === "null" ? 0 : vesselAttributes.sfocReefer ?? 0,

			fuelTypeIdKey:
				typeof vesselAttributes.fuelTypeIdKey === "string"
					? vesselAttributes.fuelTypeIdKey.trim() === ""
						? null
						: vesselAttributes.fuelTypeIdKey
					: vesselAttributes.fuelTypeIdKey == null
						? null
						: vesselAttributes.fuelTypeIdKey === 0
							? null
							: vesselAttributes.fuelTypeIdKey.toString(),
			pwind: vesselAttributes.pwind === "null" ? 0 : vesselAttributes.pwind ?? 0,
			pprop: vesselAttributes.pprop === "null" ? 0 : vesselAttributes.pprop ?? 0,
			vesselCIICorrectionFactorRequests: transformData(payloadData.ciiCorrectionsList, "ciiCorrectionFactor", "ciiCorrectionFactorValue", type),
			vesselEnginePowerRequests: vesselEngineTypeRequest.vesselEnginePowerRequests,
			vesselTechnicalEfficiencyRequests: vesselTechnicalEfficiencyRequests,
			vesselConsumerRequests:
				consumerRegistryPayload?.length > 0
					? removeNullRows(Array.isArray(consumerRegistryPayload) ? consumerRegistryPayload : JSON.parse(consumerRegistryPayload))
					: [],
			fuelRegistryV2Requests: fuelRegistryPayload?.length > 0
				? Array.isArray(fuelRegistryPayload) ? fuelRegistryPayload : JSON.parse(fuelRegistryPayload) : [],
			vesselCapacityChangeRequests: vesselCapacityChangeRequests,
		};

		return enrollVesselPayload;
	};

	// method to submit the form
	const onSubmit = (data) => {
		try {
			const riskAssessmentValues = {
				inherentRiskAssessmentMstId: Number(
					methods.getValues("inherentRisk")?.value ?? methods.getValues("vesselData.InherentRiskAssessmentMst.RiskAssessmentMstId")
				),
				controlRiskAssessmentMstId: Number(
					methods.getValues("controlRisk")?.value ?? methods.getValues("vesselData.ControlRiskAssessmentMst.RiskAssessmentMstId")
				),
			};

			data.riskAssessment = riskAssessmentValues;
			if (!vesselMstId) {
				const payload = handleAddVesselPayload(data, "enroll");
				// Condition 1: vesselMstId is not present(Enroll), call add API
				setIsLoading(true);
				axiosHttp({
					url: `${baseURL}api/MyFleet/EnrollVessel`,
					method: "POST",
					data: payload,
				})
					.then((res) => {
						setIsLoading(false);
						if (res?.data?.isSuccess) {
							emitToasterMsg("success", "Vessel", "Enrolled Successfully");
							methods.setValue("technicalEfficiencyList.0.technicalEfficiencyText", "");
							methods.setValue("technicalEfficiencyList.1.technicalEfficiencyText", "");
							navigate("/my-fleet");
						} else {
							emitToasterMsg("error", "Vessel", res?.data?.errorMessages[0]);
						}
						// res?.data?.errorCode
					})
					.catch((error) => {
						setIsLoading(false);
						emitToasterMsg("error", "Vessel", error.response?.data?.errorMessages[0]);
					});
			} else if (vesselMstId && regulations?.trim() !== "EUMRV" && regulations?.trim() !== "UKMRV" && regulations?.trim() !== "DCS" && regulations?.trim() !== "FEU" && regulations?.trim() !== "UKETS") {
				// Condition 2: vesselMstId is present (Edit) and regulations are null, call update API
				let payload = handleAddVesselPayload(data, "edit");
				payload.vesselMstId = vesselMstId;
				setIsLoading(true);
				axiosHttp({
					url: `${baseURL}api/MyFleet/EditVessel?vesselMstId=${vesselMstId}`,
					method: "POST",
					data: payload,
				})
					.then((res) => {
						setIsLoading(false);
						if (res?.status && res?.data?.isSuccess) {
							emitToasterMsg("success", "Vessel", "Updated Successfully");
							if (vesselMstId && isEnrollVessel) {
								navigate("/my-fleet");
							} else {
								getVesselDetailsByVesselMstId(vesselMstId);
							}
						} else {
							emitToasterMsg("error", "Vessel", res?.data?.errorMessages[0]);
						}
					})
					.catch((error) => {
						setIsLoading(false);
						emitToasterMsg("error", "Vessel", error.response?.data?.errorMessages[0]);
					});
			} else if (vesselMstId && (regulations?.trim() === "EUMRV" || regulations?.trim() === "UKMRV" || regulations?.trim() === "DCS" || regulations?.trim() === "FEU" || regulations?.trim() === "UKETS")) {
				// Condition 2: vesselMstId is present(Child report vessel edit) and regulations are not null, call update API
				let payload = handleAddVesselPayload(data, "edit");
				let childReportPayload = {
					vesselName: payload?.vesselName,
					callSign: payload?.callSign,
					portCodeMstId: payload?.portCodeMstId,
					vesselConsumerRequests: payload?.vesselConsumerRequests,
				};
				// UK ETS-specific field
				if (regulations?.trim() === "UKETS") {
					childReportPayload.ukEtsMonitoringPlanStatusMstId =
						data?.ukMets?.monitoringPlanStatus
							? Number(data.ukMets.monitoringPlanStatus)
							: null;
				}
				setChildPayload(childReportPayload);
				isOpenModal(true);
			}
		} catch (error) {
			setIsLoading(false);
			emitToasterMsg("error", "Vessel", error?.message);
		}
	};

	const renderFormGroup = (title, FormComponent, props) => (
		<div className="space-btwn-columns">
			<FormTitle title={title} />
			<FormComponent {...props} />
		</div>
	);

	//method to update child report vessel details
	const handleChildReportVesselDetailsSave = () => {
		axiosHttp({
			url: `${baseURL}api/MyFleet/EditChildReportVessel?vesselMstId=${vesselMstId}`,
			method: "POST",
			data: childPayload,
		})
			.then((res) => {
				setIsLoading(false);
				if (res?.status && res?.data?.isSuccess) {
					isOpenModal(false);
					emitToasterMsg("success", "Child Report Vessel", "Update Successfully");
					getVesselDetailsByVesselMstId(vesselMstId);
				} else {
					isOpenModal(false);
					emitToasterMsg("error", "Child Report Vessel", res?.data?.errorCode);
				}
			})
			.catch((error) => {
				isOpenModal(false);
				setIsLoading(false);
				emitToasterMsg("error", "Vessel", error?.response?.data?.errorMessage);
			});
	};

	const baseData = new VesselDetailsModel(masterData, vesselData, vesselMstId, disabled, isEnrollVessel, regulations);

	return (
		<>
			{Object.keys(masterData).length > 0 && (
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit(onSubmit, handleFormError)}>
						{isEnrollVessel ? (
							<div className="bread-crumb ml-4 pt-2">
								<BreadCrumb items={breadcrumb} />
							</div>
						) : null}
						<MDBRow className="mb-4 mx-2">
							<MDBCol md="6">
								{renderFormGroup(
									"Management/Submitter",
									ManagementSubmitterFormGroup,
									new ManagementSubmitterFormGroupModel(baseData, disableRiskAssessment)
								)}
								<div className="mt-3 pt-1">
									{renderFormGroup(
										"Vessel Identification",
										VesselIdentificationFormGroup,
										new VesselIdentificationFormGroupModel(baseData, imoBasedData, setImoBasedData)
									)}
								</div>
							</MDBCol>
							<MDBCol md="6">
								{renderFormGroup(
									"Vessel Particulars",
									VesselParticularsFormGroup,
									new VesselParticularsFormGroupModel(
										baseData,
										imoBasedData,
										setSumofMainEnginePowers,
										sumofMainEnginePowers,
										setSumofAuxEnginePowers,
										sumofAuxEnginePowers										
									)
								)}

								{regulations?.trim() === "UKETS" && !isParentReport && !isEnrollVessel && 
								(
								<div className="mt-3 pt-1">
									{renderFormGroup(
										"UK METS",
										UKMETSFormGroup,
										new UKMETSFormGroupModel(baseData,reportStatus)
									)}
								</div>
								)}

							</MDBCol>
						</MDBRow>

						<MDBRow className="mb-4 mx-2">
							<MDBCol md="6">{renderFormGroup("Regulation Details", RegulationDetailsmFormGroup, baseData)}</MDBCol>
							<MDBCol md="6">{renderFormGroup("CII Correction Factors", CIICorrectionFactorsFormGroup, baseData)}</MDBCol>
						</MDBRow>
						<MDBRow className="mb-4 mx-2">
							<MDBCol md="6">
								{renderFormGroup(
									"Vessel Consumer Registry",
									ConsumerRegistryFormGroup,
									new ConsumerRegistryFormGroupModel(baseData, setSumofMainEnginePowers, isReportTab)
								)}
							</MDBCol>
							<MDBCol md="6">
								<>
									{renderFormGroup("CII Corrections for Reefer Container", CorrectionsElectricPowerFormGroup, baseData)}
									{renderFormGroup("Wind-Assisted Propulsion Systems (WAPS) ", WapsFormGroup, baseData)}
								</>
							</MDBCol>
						</MDBRow>

						<MDBRow className="mb-4 mx-2">
							<MDBCol md="12">
								{renderFormGroup(
									"Vessel Fuel Registry",
									BlendFuelRegistry,
									new FuelRegistryFormGroupModel(baseData, setSumofAuxEnginePowers, isReportTab)
								)}
							</MDBCol>
						</MDBRow>

						<MDBRow className="mb-4 mx-2">
							<MDBCol md="12">{renderFormGroup("Remarks", CommentsFormGroup, baseData)}</MDBCol>
						</MDBRow>

						{(!isVerificationComplete && (!isParentReport || isEnrollVessel)) ? (
							<div className="mt-3 save-cancel-btn-wrapper">
								<div className="d-flex justify-content-center">
									<button
										id={vesselMstId ? "enroll_vessel_save_button" : "enroll_vesel_enroll_button"}
										className="btn mr-2 enroll-submit-btn"
									>
										{vesselMstId
											? (isLoading ? "Saving..." : "Save")
											: (isLoading ? "Enrolling..." : "Enroll")}
									</button>

									{isEnrollVessel && (
										<button
											id="enroll_vessel_cancel_button"
											className="btn fleet-cancel-btn"
											type="button"
											onClick={handleCancel}
										>
											Cancel
										</button>
									)}
								</div>
							</div>
						) : (
							""
						)}
					</form>
				</FormProvider>
			)}
			{/* display modal for child report on submit */}
			<div className="confirmation-modal-container">
				<Modal
					isOpen={openModal}
					modalHeader
					headerHeight={"20px"}
					showFooter
					onCloseClickListener={() => {
						isOpenModal(false);
					}}
					footer={
						<div className="d-flex justify-content-end fixed-action-btns p-4">
							<Button
								className="modal-delete-btn"
								onClick={() => handleChildReportVesselDetailsSave()}
								primaryColor={COLORS.RED.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
							>
								Ok
							</Button>
							<Button
								className="modal-cancel-btn"
								onClick={() => {
									isOpenModal(false);
								}}
							>
								Cancel
							</Button>
						</div>
					}
				>
					<div>
						<h4>
							<strong>Confirmation</strong>
						</h4>
						<hr></hr>
						<p>Please note:</p>
						<ul>
							<li>The amendments made in the child report will not be changed in MyFleet.</li>
							<li>Also, these changes do not reflect on any other reports.</li>
						</ul>

						<hr />
					</div>
				</Modal>
			</div>
		</>
	);
};

export default VesselDetails;
